/**
 * Unified Registration Page Component (Simplified)
 * مكون صفحة التسجيل الموحدة (مبسط)
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Copy,
  Eye,
  EyeOff,
  Link2,
  School,
  UserRound,
  UsersRound,
  XCircle,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import {
  StudentRegistrationData,
  ParentRegistrationData,
  GRADES,
} from '../../types/registration';
import { useRegistration } from '../../hooks/useRegistration';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Surface from '../../components/ui/Surface';
import ClassTechLogo from '../../components/brand/ClassTechLogo';

const selectClassName =
  'min-h-11 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm text-secondary-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:cursor-not-allowed disabled:bg-secondary-100 disabled:text-secondary-500 dark:border-secondary-700 dark:bg-secondary-900 dark:text-white dark:disabled:bg-secondary-800';

const fieldLabelClassName =
  'block text-sm font-semibold text-secondary-700 dark:text-secondary-200';

const UnifiedRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    currentStep,
    selectedUserType,
    isLoading,
    validationErrors,
    registrationResult,
    formData,
    codeValidation,
    updateFormData,
    handleUserTypeSelection,
    handleSubmit,
    goBack,
  } = useRegistration();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // إرسال النموذج
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmit();
  };

  // نسخ الكود
  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success('تم نسخ الكود.');
    } catch {
      toast.error('تعذر نسخ الكود تلقائياً. حدده وانسخه يدوياً.');
    }
  };

  // خطوة اختيار نوع المستخدم
  const renderUserTypeSelection = () => (
    <div className='min-h-screen bg-brand-background dark:bg-secondary-950 flex items-center justify-center p-4'>
      <div className='max-w-lg w-full'>
        <div className='text-center mb-8'>
          <ClassTechLogo size='lg' showTagline className='justify-center' />
          <h1 className='mt-6 text-3xl font-bold text-brand-navy dark:text-white mb-2'>
            أنشئ حسابك في Class Tech
          </h1>
          <p className='text-brand-text-secondary dark:text-gray-300'>
            اختر نوع حسابك للمتابعة إلى تجربة مصممة لدورك
          </p>
        </div>

        <div className='space-y-4'>
          {/* كارت الطالب */}
          <button
            onClick={() => handleUserTypeSelection('student')}
            className='w-full p-6 bg-white dark:bg-secondary-900 rounded-xl shadow-surface hover:shadow-lg transition-all duration-200 border border-secondary-200 dark:border-secondary-800 hover:border-primary-400 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
          >
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-4 space-x-reverse'>
                <div className='w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center group-hover:bg-primary-100 transition-colors'>
                  <UserRound className='h-6 w-6 text-primary-700' />
                </div>
                <div className='text-right'>
                  <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                    أنا طالب
                  </h3>
                  <p className='text-sm text-gray-500 dark:text-gray-400'>
                    أريد تسجيل حساب جديد كطالب
                  </p>
                </div>
              </div>
              <ArrowLeft className='h-5 w-5 text-gray-400 group-hover:text-primary-600 transition-colors' />
            </div>
          </button>

          {/* كارت ولي الأمر */}
          <button
            onClick={() => handleUserTypeSelection('parent')}
            className='w-full p-6 bg-white dark:bg-secondary-900 rounded-lg shadow-surface hover:shadow-lg transition-all duration-200 border border-secondary-200 dark:border-secondary-800 hover:border-success-400 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success-500'
          >
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-4 space-x-reverse'>
                <div className='w-12 h-12 bg-success-50 rounded-lg flex items-center justify-center group-hover:bg-success-100 transition-colors'>
                  <UsersRound className='h-6 w-6 text-success-700' />
                </div>
                <div className='text-right'>
                  <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                    أنا ولي أمر
                  </h3>
                  <p className='text-sm text-gray-500 dark:text-gray-400'>
                    أريد تسجيل حساب جديد كولي أمر
                  </p>
                </div>
              </div>
              <ArrowLeft className='h-5 w-5 text-gray-400 group-hover:text-green-600 transition-colors' />
            </div>
          </button>

          {/* كارت المدرس */}
          <button
            onClick={() => navigate('/register-teacher')}
            className='w-full p-6 bg-white dark:bg-secondary-900 rounded-lg shadow-surface hover:shadow-lg transition-all duration-200 border border-secondary-200 dark:border-secondary-800 hover:border-primary-400 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
          >
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-4 space-x-reverse'>
                <div className='w-12 h-12 bg-cyan-50 rounded-lg flex items-center justify-center group-hover:bg-cyan-100 transition-colors'>
                  <School className='h-6 w-6 text-brand-cyan' />
                </div>
                <div className='text-right'>
                  <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                    أنا مدرس
                  </h3>
                  <p className='text-sm text-gray-500 dark:text-gray-400'>
                    أريد الانضمام كمدرس في المنصة
                  </p>
                </div>
              </div>
              <ArrowLeft className='h-5 w-5 text-gray-400 group-hover:text-primary-600 transition-colors' />
            </div>
          </button>
        </div>

        <div className='mt-8 text-center'>
          <p className='text-gray-600 dark:text-gray-300'>
            لديك حساب بالفعل؟{' '}
            <button
              onClick={() => navigate('/login')}
              className='text-primary-600 hover:text-primary-800 font-medium'
            >
              تسجيل الدخول
            </button>
          </p>
        </div>
      </div>
    </div>
  );

  // نموذج التسجيل
  const renderRegistrationForm = () => (
    <div className='min-h-screen bg-brand-background dark:bg-secondary-950 flex items-center justify-center p-4'>
      <div className='max-w-md w-full'>
        {/* Header */}
        <div className='text-center mb-8'>
          <Button
            onClick={goBack}
            variant='ghost'
            className='mb-4'
            leadingIcon={<ArrowRight className='h-4 w-4' />}
          >
            العودة
          </Button>

          <div
            className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
              selectedUserType === 'student' ? 'bg-blue-100' : 'bg-green-100'
            }`}
          >
            {selectedUserType === 'student' ? (
              <UserRound className='h-8 w-8 text-blue-700' />
            ) : (
              <UsersRound className='h-8 w-8 text-green-700' />
            )}
          </div>

          <h1 className='text-2xl font-bold text-gray-900 dark:text-white mb-2'>
            {selectedUserType === 'student'
              ? 'تسجيل حساب طالب'
              : 'تسجيل حساب ولي أمر'}
          </h1>
          <p className='text-gray-600 dark:text-gray-300'>
            {selectedUserType === 'student'
              ? 'أدخل بياناتك للانضمام إلى منصة التعليم'
              : 'أدخل بياناتك لمتابعة تقدم طفلك'}
          </p>
        </div>

        {/* النموذج */}
        <Surface padding='lg'>
          <form onSubmit={onSubmit} className='space-y-6'>
          {/* الاسم */}
            <Input
              label='الاسم الكامل *'
              type='text'
              value={formData.name || ''}
              onChange={e => updateFormData('name', e.target.value)}
              placeholder='أدخل اسمك الكامل'
              disabled={isLoading}
              error={validationErrors.name}
            />

          {/* البريد الإلكتروني */}
            <Input
              label='البريد الإلكتروني *'
              type='email'
              value={formData.email || ''}
              onChange={e => updateFormData('email', e.target.value)}
              placeholder='example@email.com'
              disabled={isLoading}
              error={validationErrors.email}
            />

          {/* رقم الهاتف */}
            <Input
              label='رقم الهاتف *'
              type='tel'
              value={formData.phone || ''}
              onChange={e => updateFormData('phone', e.target.value)}
              placeholder='+966501234567'
              disabled={isLoading}
              error={validationErrors.phone}
            />

          {/* الحقول المخصصة للطالب */}
          {selectedUserType === 'student' && (
            <>
              {/* الصف الدراسي */}
              <div>
                <label className={fieldLabelClassName}>
                  الصف الدراسي *
                </label>
                <select
                  value={(formData as StudentRegistrationData).grade || ''}
                  onChange={e => updateFormData('grade', e.target.value)}
                  className={`${selectClassName} ${
                    validationErrors.grade
                      ? 'border-danger-400 focus:border-danger-500 focus:ring-danger-200 dark:border-danger-700'
                      : ''
                  }`}
                  disabled={isLoading}
                  title='اختر الصف الدراسي'
                >
                  <option value=''>اختر الصف الدراسي</option>
                  {GRADES.map(grade => (
                    <option key={grade} value={grade}>
                      {grade}
                    </option>
                  ))}
                </select>
                {validationErrors.grade && (
                  <p className='mt-1 text-xs text-danger-600 dark:text-danger-300'>
                    {validationErrors.grade}
                  </p>
                )}
              </div>

              {/* كود ولي الأمر (اختياري) */}
              <Input
                  label='كود ولي الأمر (اختياري)'
                  type='text'
                  value={(formData as StudentRegistrationData).parentCode || ''}
                  onChange={e =>
                    updateFormData('parentCode', e.target.value.toUpperCase())
                  }
                  placeholder='P-XXXXXXXX'
                  maxLength={10}
                  disabled={isLoading}
                  error={validationErrors.parentCode}
                  hint='إذا أدخلت كود ولي الأمر فسيتم حفظه بانتظار اعتماد الربط داخل مساحة المدرس.'
                />
            </>
          )}

          {/* الحقول المخصصة لولي الأمر */}
          {selectedUserType === 'parent' && (
            <>
              {/* كود الطالب */}
              <div>
                <label className={fieldLabelClassName}>
                  كود الطالب *
                </label>
                <div className='mb-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200'>
                  <div className='flex items-start space-x-2 space-x-reverse'>
                    <span className='text-yellow-600 text-lg flex-shrink-0'>
                      <AlertTriangle className='h-5 w-5' />
                    </span>
                    <div className='text-xs text-yellow-800'>
                      <p className='font-medium mb-1'>مهم جداً:</p>
                      <p>
                        سيظهر اسم الطالب بعد إدخال الكود للتأكد من أن الكود يخص
                        أبنك فعلاً
                      </p>
                    </div>
                  </div>
                </div>
                <Input
                  type='text'
                  value={(formData as ParentRegistrationData).studentCode || ''}
                  onChange={e =>
                    updateFormData('studentCode', e.target.value.toUpperCase())
                  }
                  className={
                    codeValidation.isValid
                      ? 'border-success-400 focus:border-success-500 focus:ring-success-200'
                      : undefined
                  }
                  placeholder='أدخل كود الطالب للربط (مثال: S-ABC12345)'
                  maxLength={10}
                  disabled={isLoading}
                  error={validationErrors.studentCode}
                />

                {/* حالة التحقق من الكود */}
                {codeValidation.isChecking && (
                  <div className='mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200'>
                    <div className='flex items-center space-x-2 space-x-reverse'>
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600'></div>
                      <p className='text-sm text-blue-700 font-medium'>
                        جاري التحقق من الكود...
                      </p>
                    </div>
                  </div>
                )}

                {codeValidation.isValid && codeValidation.studentName && (
                  <div className='mt-3 p-4 bg-green-50 rounded-lg border border-green-200'>
                    <div className='flex items-start space-x-3 space-x-reverse'>
                      <div className='flex-shrink-0'>
                        <div className='w-10 h-10 bg-green-100 rounded-full flex items-center justify-center'>
                          <UserRound className='h-5 w-5 text-green-600' />
                        </div>
                      </div>
                      <div className='flex-1'>
                        <h4 className='text-sm font-semibold text-green-800 mb-2'>
                          تم العثور على الطالب
                        </h4>
                        <div className='space-y-1'>
                          <p className='text-sm text-green-700'>
                            <strong>الاسم:</strong> {codeValidation.studentName}
                          </p>
                          {codeValidation.studentGrade && (
                            <p className='text-sm text-green-700'>
                              <strong>الصف:</strong>{' '}
                              {codeValidation.studentGrade}
                            </p>
                          )}
                        </div>
                        <div className='mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded'>
                          <p className='text-xs text-yellow-800 font-medium flex items-center'>
                            <AlertTriangle className='ml-1 inline h-4 w-4' />
                            تأكد من أن هذا هو طفلك قبل المتابعة
                          </p>
                        </div>
                        <p className='text-xs text-green-600 mt-2'>
                          سيتم إنشاء الحساب وحفظ طلب الربط بانتظار اعتماد المدرس
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {codeValidation.error && !codeValidation.isChecking && (
                  <div className='mt-3 p-3 bg-red-50 rounded-lg border border-red-200'>
                    <div className='flex items-center space-x-2 space-x-reverse'>
                      <XCircle className='h-5 w-5 text-red-500' />
                      <p className='text-sm text-red-700 font-medium'>
                        {codeValidation.error}
                      </p>
                    </div>
                    <p className='text-xs text-red-600 mt-1 mr-6'>
                      تأكد من صحة الكود أو اطلبه من المدرس/المدرسة
                    </p>
                  </div>
                )}

              </div>

              {/* المهنة (اختياري) */}
              <Input
                  label='المهنة (اختياري)'
                  type='text'
                  value={(formData as ParentRegistrationData).occupation || ''}
                  onChange={e => updateFormData('occupation', e.target.value)}
                  placeholder='أدخل مهنتك'
                  disabled={isLoading}
                />
            </>
          )}

          {/* كلمة المرور */}
          <div>
            <div className='relative'>
              <Input
                label='كلمة المرور *'
                type={showPassword ? 'text' : 'password'}
                value={formData.password || ''}
                onChange={e => updateFormData('password', e.target.value)}
                className='pl-12'
                placeholder='أدخل كلمة المرور'
                disabled={isLoading}
                error={validationErrors.password}
              />
              <button
                type='button'
                onClick={() => setShowPassword(!showPassword)}
                className='absolute left-2 top-[2.25rem] flex h-9 w-9 items-center justify-center rounded-lg text-secondary-500 hover:bg-secondary-100 hover:text-secondary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-secondary-800'
                aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
                aria-pressed={showPassword}
              >
                {showPassword ? (
                  <EyeOff className='h-5 w-5' />
                ) : (
                  <Eye className='h-5 w-5' />
                )}
              </button>
            </div>
          </div>

          {/* تأكيد كلمة المرور */}
          <div>
            <div className='relative'>
              <Input
                label='تأكيد كلمة المرور *'
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword || ''}
                onChange={e =>
                  updateFormData('confirmPassword', e.target.value)
                }
                className='pl-12'
                placeholder='أعد إدخال كلمة المرور'
                disabled={isLoading}
                error={validationErrors.confirmPassword}
              />
              <button
                type='button'
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className='absolute left-2 top-[2.25rem] flex h-9 w-9 items-center justify-center rounded-lg text-secondary-500 hover:bg-secondary-100 hover:text-secondary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-secondary-800'
                aria-label={
                  showConfirmPassword
                    ? 'إخفاء تأكيد كلمة المرور'
                    : 'إظهار تأكيد كلمة المرور'
                }
                aria-pressed={showConfirmPassword}
              >
                {showConfirmPassword ? (
                  <EyeOff className='h-5 w-5' />
                ) : (
                  <Eye className='h-5 w-5' />
                )}
              </button>
            </div>
          </div>

          <div className='rounded-lg border border-secondary-200 bg-secondary-50 p-4 dark:border-secondary-800 dark:bg-secondary-900/60'>
            <label className='flex items-start gap-3 text-sm leading-7 text-secondary-700 dark:text-secondary-200'>
              <input
                type='checkbox'
                checked={Boolean(formData.acceptTerms)}
                onChange={e => updateFormData('acceptTerms', e.target.checked)}
                disabled={isLoading}
                className='mt-1 h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-60'
              />
              <span>
                أوافق على{' '}
                <Link
                  to='/terms'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='font-semibold text-primary-700 hover:text-primary-900 dark:text-primary-300'
                >
                  شروط الاستخدام
                </Link>{' '}
                و{' '}
                <Link
                  to='/privacy'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='font-semibold text-primary-700 hover:text-primary-900 dark:text-primary-300'
                >
                  سياسة الخصوصية
                </Link>{' '}
                    الخاصة بمنصة Class Tech.
              </span>
            </label>
            {validationErrors.acceptTerms && (
              <p className='mt-2 text-xs text-danger-600 dark:text-danger-300'>
                {validationErrors.acceptTerms}
              </p>
            )}
          </div>

          {/* رسالة الخطأ العامة */}
          {registrationResult && !registrationResult.success && (
            <div className='p-4 bg-red-50 border border-red-200 rounded-lg'>
              <div className='flex items-center'>
                <span className='text-red-800'>
                  {registrationResult.error}
                </span>
              </div>
            </div>
          )}

          {/* تأكيد البيانات لولي الأمر */}
          {selectedUserType === 'parent' && codeValidation.isValid && (
            <div className='p-4 bg-amber-50 border border-amber-200 rounded-lg'>
              <div className='flex items-start space-x-2 space-x-reverse'>
                <AlertTriangle className='h-5 w-5 flex-shrink-0 text-amber-600' />
                <div className='text-sm'>
                  <p className='font-semibold text-amber-800 mb-2'>
                    تأكيد مهم قبل إنشاء الحساب:
                  </p>
                  <p className='text-amber-700 mb-2'>
                    أنت على وشك إنشاء حساب ولي أمر وحفظ طلب ربط للطالب:
                    <strong className='block mt-1'>
                      {codeValidation.studentName}
                      {codeValidation.studentGrade &&
                        ` (${codeValidation.studentGrade})`}
                    </strong>
                  </p>
                  <p className='text-amber-700 text-xs'>
                    الربط التشغيلي لا يكتمل إلا بعد اعتماد المدرس داخل مساحة الطالب
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* زر الإرسال */}
            <Button
            type='submit'
            disabled={
              isLoading ||
              (selectedUserType === 'parent' && !codeValidation.isValid)
            }
            variant={selectedUserType === 'student' ? 'primary' : 'success'}
            size='lg'
            loading={isLoading}
            className='w-full'
          >
            {`إنشاء حساب ${
                selectedUserType === 'student' ? 'طالب' : 'ولي أمر'
              }`}
            </Button>

          {/* رسالة للأزرار المعطلة */}
            {selectedUserType === 'parent' && !codeValidation.isValid && (
            <p className='text-xs text-gray-500 text-center mt-2'>
              يجب إدخال كود طالب صحيح لإنشاء حساب ولي الأمر
            </p>
            )}
          </form>
        </Surface>

        {/* رابط تسجيل الدخول */}
        <div className='mt-6 text-center'>
          <p className='text-gray-600'>
            لديك حساب بالفعل؟{' '}
            <button
              onClick={() => navigate('/login')}
              className='text-primary-600 hover:text-primary-800 font-medium'
            >
              تسجيل الدخول
            </button>
          </p>
        </div>
      </div>
    </div>
  );

  // صفحة النجاح
  const renderSuccessPage = () => (
    <div className='min-h-screen bg-brand-background dark:bg-secondary-950 flex items-center justify-center p-4'>
      <div className='max-w-md w-full'>
        <div className='bg-white p-8 rounded-xl shadow-lg text-center dark:bg-secondary-900'>
          <div className='w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center'>
            <CheckCircle2 className='h-8 w-8 text-green-600' />
          </div>

          <h1 className='text-2xl font-bold text-gray-900 dark:text-white mb-4'>
            تم إنشاء حسابك بنجاح!
          </h1>

          <p className='text-gray-600 dark:text-gray-300 mb-6'>
            مرحباً بك في منصة Class Tech. تم إنشاء حسابك كـ
            {selectedUserType === 'student' ? 'طالب' : 'ولي أمر'} بنجاح.
          </p>

          {/* معلومات الربط */}
          {registrationResult?.linkedUserId && (
            <div className='bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6'>
              <div className='flex items-center justify-center mb-3'>
                <div className='w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-2'>
                  <Link2 className='h-4 w-4 text-primary-600' />
                </div>
                <p className='text-blue-800 font-semibold'>
                  الربط بانتظار الاعتماد
                </p>
              </div>
              <p className='text-sm text-blue-700 mb-2'>
                {selectedUserType === 'student'
                  ? 'تم حفظ كود ولي الأمر ضمن بيانات التسجيل.'
                  : 'تم حفظ كود الطالب، وسيكتمل الربط بعد اعتماد المدرس.'}
              </p>
              {selectedUserType === 'parent' && codeValidation.studentName && (
                <div className='text-xs text-primary-600 bg-white rounded p-2 mt-2'>
                  <strong>الطالب المرتبط:</strong> {codeValidation.studentName}
                  {codeValidation.studentGrade &&
                    ` (${codeValidation.studentGrade})`}
                </div>
              )}
            </div>
          )}

          {/* الكود المُنشأ */}
          {registrationResult?.generatedCode && (
            <div className='bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6'>
              <p className='font-medium text-gray-900 mb-2'>كودك الخاص:</p>
              <div className='flex items-center justify-between bg-white border rounded-lg p-3'>
                <span className='font-mono text-lg font-bold text-primary-600'>
                  {registrationResult.generatedCode}
                </span>
                <button
                  type='button'
                  onClick={() => copyCode(registrationResult.generatedCode!)}
                  className='flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
                  aria-label='نسخ الكود'
                >
                  <Copy className='h-4 w-4' />
                </button>
              </div>
              <p className='text-xs text-gray-500 mt-2'>
                احفظ هذا الكود ليتمكن{' '}
                {selectedUserType === 'student' ? 'ولي أمرك' : 'طفلك'} من ربط
                حسابه معك
              </p>
            </div>
          )}

          {/* التحذيرات */}
          {registrationResult?.warnings &&
            registrationResult.warnings.length > 0 && (
              <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6'>
                <p className='font-medium text-yellow-800 mb-2'>تنبيهات:</p>
                {registrationResult.warnings.map((warning, index) => (
                  <p key={index} className='text-sm text-yellow-700'>
                    • {warning}
                  </p>
                ))}
              </div>
            )}

          {/* أزرار الإجراءات */}
          <div className='space-y-3'>
            <button
              onClick={() => navigate('/login')}
              className='w-full py-3 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors'
            >
              تسجيل الدخول الآن
            </button>

            <button
              onClick={() => navigate('/')}
              className='w-full py-3 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors'
            >
              العودة للصفحة الرئيسية
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // العرض الرئيسي
  return (
    <div className='font-arabic'>
      {currentStep === 'userType' && renderUserTypeSelection()}
      {currentStep === 'form' && renderRegistrationForm()}
      {currentStep === 'success' && renderSuccessPage()}
    </div>
  );
};

export default UnifiedRegistrationPage;
